/**
 * ============================================================================
 * DispositionTaskRow — Phase 6a frontend (T-P6a-W3-03)
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: a single task card in the disposition tray. Renders the doc id +
 *          title, retention-end relative time, cooling-off countdown, action
 *          badge, two-signer status, blocked reason, and the two role-scoped
 *          sign buttons.
 *
 * A11Y:
 *   - The card uses an <article> with aria-labelledby on the title.
 *   - Signer status uses both an icon and a label ("Signed" / "Pending") —
 *     never colour alone (WCAG 1.4.1).
 *   - The "Why?" affordance for BLOCKED tasks is a <button> with an
 *     accessible name — not a hover-only tooltip.
 * ============================================================================ */

import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import styles from './RecordsDispositionTray.module.css';
import type {
  DispositionBlockedReason,
  DispositionTask,
  SignerRole,
} from './useDispositionTasksQuery';

export interface DispositionTaskRowProps {
  task: DispositionTask;
  onSign: (role: SignerRole) => void;
}

export function DispositionTaskRow({ task, onSign }: DispositionTaskRowProps) {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const whyId = useId();
  const [showWhy, setShowWhy] = useState(false);

  const rtf = useMemo(
    () => new Intl.RelativeTimeFormat(i18n.language, { numeric: 'auto' }),
    [i18n.language],
  );

  const retentionEndedDays = useMemo(() => {
    const ms = Date.now() - new Date(task.retention_end_at_utc).getTime();
    return Math.floor(ms / (24 * 60 * 60 * 1000));
  }, [task.retention_end_at_utc]);

  const coolingOffDays = useMemo(() => {
    if (!task.cooling_off_until_utc) return null;
    const ms = new Date(task.cooling_off_until_utc).getTime() - Date.now();
    return Math.ceil(ms / (24 * 60 * 60 * 1000));
  }, [task.cooling_off_until_utc]);

  const qaSigner = task.signers.find((s) => s.role === 'QA_MANAGER');
  const dcSigner = task.signers.find((s) => s.role === 'DOC_CONTROLLER');

  const isBlocked = task.state === 'BLOCKED';

  return (
    <article className={styles.card} aria-labelledby={titleId}>
      <header className={styles.cardHeader}>
        <div className={styles.cardTitleWrap}>
          <h3 id={titleId} className={styles.cardTitle}>
            <code className={styles.docNumber}>{task.doc_number}</code>
            {task.document_title && (
              <span className={styles.docTitle}>{task.document_title}</span>
            )}
          </h3>
          <p className={styles.cardMeta}>
            <span>{task.policy_name}</span>
            <span aria-hidden="true"> · </span>
            <span>
              {t('retention.disposition.retentionEnded', {
                defaultValue: 'retention ended {{rel}}',
                rel:
                  retentionEndedDays >= 0
                    ? rtf.format(-retentionEndedDays, 'day')
                    : rtf.format(-retentionEndedDays, 'day'),
              })}
            </span>
            {coolingOffDays !== null && coolingOffDays > 0 && (
              <>
                <span aria-hidden="true"> · </span>
                <span>
                  {t('retention.disposition.coolingOff', {
                    defaultValue: 'cooling-off ends in {{rel}}',
                    rel: rtf.format(coolingOffDays, 'day'),
                  })}
                </span>
              </>
            )}
          </p>
        </div>
        <div className={styles.cardActionBadge}>
          <span
            className={`${styles.actionBadge} ${styles[`action-${task.action}`]}`}
            aria-label={t(`retention.disposition.action.${task.action}.aria`, {
              defaultValue: `Action: ${task.action}`,
            })}
          >
            {t(`retention.disposition.action.${task.action}.label`, {
              defaultValue: task.action,
            })}
          </span>
        </div>
      </header>

      <section
        className={styles.signerGrid}
        aria-label={t('retention.disposition.signers.aria', { defaultValue: 'Required signers' })}
      >
        <SignerStatus
          role="QA_MANAGER"
          signed={qaSigner?.signed_at_utc != null}
        />
        <SignerStatus
          role="DOC_CONTROLLER"
          signed={dcSigner?.signed_at_utc != null}
        />
      </section>

      {isBlocked && task.blocked_reason && (
        <div className={styles.blockedRow} role="note">
          <span className={styles.blockedBadge}>
            {t('retention.disposition.blocked.badge', { defaultValue: 'BLOCKED' })}
          </span>
          <span>
            {t(`retention.disposition.blockedReason.${task.blocked_reason}.short`, {
              defaultValue: shortBlockedReason(task.blocked_reason),
            })}
          </span>
          <button
            type="button"
            className={styles.whyButton}
            aria-expanded={showWhy}
            aria-controls={whyId}
            onClick={() => setShowWhy((v) => !v)}
            aria-label={t('retention.disposition.blocked.whyAria', {
              defaultValue: 'Why is this task blocked?',
            })}
          >
            {t('retention.disposition.blocked.why', { defaultValue: 'Why?' })}
          </button>
          {showWhy && (
            <p id={whyId} className={styles.whyText}>
              {t(`retention.disposition.blockedReason.${task.blocked_reason}.long`, {
                defaultValue: longBlockedReason(task.blocked_reason),
              })}
            </p>
          )}
        </div>
      )}

      <footer className={styles.cardActions}>
        <Button
          size="sm"
          variant="primary"
          disabled={isBlocked || qaSigner?.signed_at_utc != null}
          onClick={() => onSign('QA_MANAGER')}
          aria-label={t('retention.disposition.actions.signQAAria', {
            defaultValue: 'Sign as QA Manager for document {{doc}}',
            doc: task.doc_number,
          })}
        >
          {t('retention.disposition.actions.signQA', { defaultValue: 'Sign as QA Manager' })}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={isBlocked || dcSigner?.signed_at_utc != null}
          onClick={() => onSign('DOC_CONTROLLER')}
          aria-label={t('retention.disposition.actions.signDCAria', {
            defaultValue: 'Sign as Doc Controller for document {{doc}}',
            doc: task.doc_number,
          })}
        >
          {t('retention.disposition.actions.signDC', { defaultValue: 'Sign as Doc Controller' })}
        </Button>
      </footer>
    </article>
  );
}

function SignerStatus({ role, signed }: { role: SignerRole; signed: boolean }) {
  const { t } = useTranslation();
  const roleLabel = t(`retention.disposition.signers.role.${role}`, {
    defaultValue: role === 'QA_MANAGER' ? 'QA Manager' : 'Doc Controller',
  });
  const stateLabel = signed
    ? t('retention.disposition.signers.signed', { defaultValue: 'Signed' })
    : t('retention.disposition.signers.pending', { defaultValue: 'Pending' });
  return (
    <div
      className={`${styles.signerRow} ${signed ? styles.signed : styles.pending}`}
      aria-label={`${roleLabel}: ${stateLabel}`}
    >
      <span aria-hidden="true" className={styles.signerIcon}>
        {signed ? '✓' : '◯'}
      </span>
      <span className={styles.signerRole}>{roleLabel}</span>
      <span className={styles.signerState}>{stateLabel}</span>
    </div>
  );
}

function shortBlockedReason(r: DispositionBlockedReason): string {
  switch (r) {
    case 'OPEN_CAPA': return 'Open CAPA references this record.';
    case 'PENDING_CC': return 'Pending change control references this record.';
    case 'OPEN_LEGAL_HOLD': return 'Legal hold is active on this record.';
  }
}

function longBlockedReason(r: DispositionBlockedReason): string {
  switch (r) {
    case 'OPEN_CAPA':
      return 'A CAPA in the OPEN state references this document. Records may not be destroyed while a corrective-action investigation is live; close or detach the CAPA first.';
    case 'PENDING_CC':
      return 'A change control referencing this document is awaiting QA approval. The record must remain intact until the change is approved or rejected.';
    case 'OPEN_LEGAL_HOLD':
      return 'A legal hold has been placed on this record. Disposition is blocked until the hold is released by Legal.';
  }
}
